import { useMutation, UseMutationOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"
import {
  CreateBusinessPartnerRequestDto,
  findContactsByCreatedByIdQueryKey,
} from "~/gen/api"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const useCreateContactMutation = (
  organizationId: string,
  options?: UseMutationOptions<
    string,
    AxiosError,
    CreateBusinessPartnerRequestDto
  >
) => {
  const { t } = useTranslation("traceability")

  return useMutation({
    ...options,
    mutationFn: async (variables) =>
      (
        await httpClient.post<string>(
          "v1/contacts/create-business-partner",
          variables
        )
      ).data,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: findContactsByCreatedByIdQueryKey(organizationId),
      })
      showSuccessNotification({
        title: t("contactModal.messages.create.success", {
          email: variables.email,
        }),
      })
      await queryClient.invalidateQueries({
        queryKey: [{ url: "/v1/client-settings/find-supplier-mapping" }],
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      showAxiosError({
        notificationTitle: t("contactModal.messages.create.error", {
          error: error.response?.data ?? error.message,
        }),
        showNotification: true,
        error,
      })
      options?.onError?.(error, variables, onMutateResult, context)
    },
  })
}
